import React, { useEffect, useState } from 'react'
import { Box, Radio, Typography, styled } from '@mui/material'
import { Button, Modal } from '@/design-system'
import { Circle } from '@/types'
import { circleTypeStyle } from '../../utils'

const GroupRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 2px',
  borderBottom: '1px solid var(--ds-border)',
  cursor: 'pointer',
  '&:last-of-type': { borderBottom: 'none' },
})

const Footer = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 10,
  marginTop: 'var(--ds-space-4)',
})

export interface CirclePickerModalProps {
  open: boolean
  onClose: () => void
  /** Person this circle assignment is for */
  personName: string
  /** "Accept" for an incoming request, "Send request" for a new one */
  submitLabel: string
  circles: Circle[]
  loading?: boolean
  onSubmit: (circleID: string) => void
}

/**
 * Every connect/accept call requires at least one circle (clique) id — the
 * API has no "no circle" option — so this gate always runs before either
 * mutation fires, mirroring the legacy ConnectManagement flow.
 */
export function CirclePickerModal({
  open,
  onClose,
  personName,
  submitLabel,
  circles,
  loading,
  onSubmit,
}: CirclePickerModalProps) {
  const [selected, setSelected] = useState('')

  useEffect(() => {
    if (open) setSelected('')
  }, [open])

  return (
    <Modal open={open} onClose={onClose} title={`Who is ${personName} to you?`}>
      {circles.length === 0 ? (
        <Typography sx={{ fontSize: 13.5, color: 'var(--ds-ink-muted)' }}>
          You don&apos;t have any circles yet.
        </Typography>
      ) : (
        <Box>
          {circles.map((circle) => {
            const style = circleTypeStyle(circle.circleTypeID)
            return (
              <GroupRow key={circle.circleID} onClick={() => setSelected(circle.circleID)}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 'var(--ds-radius-md)',
                    background: style.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 18,
                    flexShrink: 0,
                    userSelect: 'none',
                  }}
                >
                  {style.emoji}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 620, color: 'var(--ds-ink)' }} noWrap>
                    {circle.circleName}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: 'var(--ds-ink-subtle)' }}>
                    {circle.connCount} member{circle.connCount === '1' ? '' : 's'}
                  </Typography>
                </Box>
                <Radio checked={selected === circle.circleID} onChange={() => setSelected(circle.circleID)} />
              </GroupRow>
            )
          })}
        </Box>
      )}
      <Footer>
        <Button variant='ghost' onClick={onClose}>
          Cancel
        </Button>
        <Button variant='primary' disabled={!selected} loading={loading} onClick={() => onSubmit(selected)}>
          {submitLabel}
        </Button>
      </Footer>
    </Modal>
  )
}
